import { useState } from "react";

const BANDS = [
  { class: 1, label: "Class 1", desc: "No impairment", wpiMin: 0,  wpiMax: 0,  color: "#22c55e" },
  { class: 2, label: "Class 2", desc: "Mild (10–25%)",    wpiMin: 10, wpiMax: 25, color: "#eab308" },
  { class: 3, label: "Class 3", desc: "Moderate (26–50%)",wpiMin: 26, wpiMax: 50, color: "#f97316" },
  { class: 4, label: "Class 4", desc: "Severe (51–100%)", wpiMin: 51, wpiMax: 100,color: "#ef4444" },
];

const MEASURES = [
  {
    key: "fvc",
    label: "FVC",
    unit: "% predicted",
    bands: [
      { class: 1, min: 80,  max: Infinity },
      { class: 2, min: 60,  max: 79 },
      { class: 3, min: 51,  max: 59 },
      { class: 4, min: 0,   max: 50 },
    ],
  },
  {
    key: "fev1",
    label: "FEV\u2081",
    unit: "% predicted",
    bands: [
      { class: 1, min: 80,  max: Infinity },
      { class: 2, min: 60,  max: 79 },
      { class: 3, min: 41,  max: 59 },
      { class: 4, min: 0,   max: 40 },
    ],
  },
  {
    key: "dlco",
    label: "D\u1d04O",
    unit: "% predicted",
    bands: [
      { class: 1, min: 70,  max: Infinity },
      { class: 2, min: 60,  max: 69 },
      { class: 3, min: 41,  max: 59 },
      { class: 4, min: 0,   max: 40 },
    ],
  },
];

function classify(value, measureBands) {
  if (value === "" || value === null || isNaN(value)) return null;
  const v = parseFloat(value);
  for (const b of measureBands) {
    if (v >= b.min && v <= b.max) return b.class;
  }
  return 4;
}

function calcWPI(value, measureBands) {
  const cls = classify(value, measureBands);
  if (cls === null) return null;
  const v = parseFloat(value);

  if (cls === 1) return 0;

  const band = measureBands.find(b => b.class === cls);
  const wpiDef = BANDS.find(b => b.class === cls);

  if (cls === 4) {
    const pftBandWidth = band.max;
    const wpiRange = wpiDef.wpiMax - wpiDef.wpiMin;
    if (pftBandWidth === 0) return wpiDef.wpiMax;
    const aboveMin = v - 0;
    const wpi = wpiDef.wpiMax - (aboveMin * wpiRange / pftBandWidth);
    return Math.min(wpiDef.wpiMax, Math.max(wpiDef.wpiMin, Math.round(wpi * 10) / 10));
  }

  const pftBandWidth = band.max - band.min;
  const wpiRange = wpiDef.wpiMax - wpiDef.wpiMin;
  const aboveMin = v - band.min;
  const wpi = wpiDef.wpiMax - (aboveMin * wpiRange / pftBandWidth);
  return Math.min(wpiDef.wpiMax, Math.max(wpiDef.wpiMin, Math.round(wpi * 10) / 10));
}

export default function RespiratoryCalculator() {
  const [values, setValues] = useState({ fvc: "", fev1: "", dlco: "" });

  const results = MEASURES.map(m => {
    const v = values[m.key];
    const cls = classify(v, m.bands);
    const wpi = calcWPI(v, m.bands);
    const clsDef = cls ? BANDS.find(b => b.class === cls) : null;
    return { ...m, value: v, cls, wpi, clsDef };
  });

  const maxClass = Math.max(...results.map(r => r.cls ?? 0));
  const maxWPI = Math.max(...results.map(r => r.wpi ?? 0));
  const overallBand = BANDS.find(b => b.class === maxClass);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 28, borderBottom: "1px solid #e5e7eb", paddingBottom: 16 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6b7280", marginBottom: 6 }}>
          AMA Guides 4th Edition · Table 8
        </div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: "#111827" }}>
          Respiratory Impairment Calculator
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7280" }}>
          Whole Person Impairment — Pulmonary Function
        </p>
      </div>

      {/* Input rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
        {results.map(r => (
          <div key={r.key} style={{
            background: "#fff",
            border: `1px solid ${r.clsDef ? r.clsDef.color + "66" : "#e5e7eb"}`,
            borderRadius: 10,
            padding: "16px 20px",
            transition: "border-color 0.3s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "0 0 auto" }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: "#111827", width: 48, display: "inline-block" }}>
                  {r.label}
                </span>
                <div style={{ position: "relative" }}>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    value={values[r.key]}
                    onChange={e => setValues(v => ({ ...v, [r.key]: e.target.value }))}
                    placeholder="—"
                    style={{
                      width: 80,
                      padding: "8px 36px 8px 12px",
                      background: "#f9fafb",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      color: "#111827",
                      fontSize: 16,
                      outline: "none",
                    }}
                  />
                  <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#6b7280", pointerEvents: "none" }}>%</span>
                </div>
              </div>

              <div style={{ flex: 1 }} />

              {r.clsDef ? (
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <span style={{
                    fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: r.clsDef.color,
                    background: r.clsDef.color + "18", padding: "4px 10px", borderRadius: 4,
                    border: `1px solid ${r.clsDef.color}44`,
                  }}>
                    {r.clsDef.label}
                  </span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: r.clsDef.color, minWidth: 64, textAlign: "right" }}>
                    {r.wpi}%
                  </span>
                  <span style={{ fontSize: 12, color: "#6b7280", minWidth: 40 }}>WPI</span>
                </div>
              ) : (
                <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>
              )}
            </div>

            {/* Band bar */}
            {r.clsDef && r.clsDef.class > 1 && (
              <div style={{ marginTop: 14 }}>
                {(() => {
                  const band = r.bands.find(b => b.class === r.cls);
                  const pftMax = band.max === Infinity ? 120 : band.max;
                  const pftMin = band.min;
                  const pftWidth = pftMax - pftMin;
                  const pos = Math.max(0, Math.min(100, ((parseFloat(r.value) - pftMin) / pftWidth) * 100));
                  return (
                    <div>
                      <div style={{ height: 4, background: "#e5e7eb", borderRadius: 2, position: "relative", overflow: "visible" }}>
                        <div style={{
                          position: "absolute", left: 0, top: 0, height: "100%",
                          width: `${pos}%`, background: `linear-gradient(90deg, ${r.clsDef.color}66, ${r.clsDef.color})`,
                          borderRadius: 2, transition: "width 0.4s ease",
                        }} />
                        <div style={{
                          position: "absolute", left: `${pos}%`, top: "50%", transform: "translate(-50%, -50%)",
                          width: 10, height: 10, borderRadius: "50%", background: r.clsDef.color,
                          border: "2px solid #fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.4s ease",
                        }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10, color: "#9ca3af" }}>
                        <span>{pftMin}% (floor)</span>
                        <span>{pftMax}% (ceiling)</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Overall result */}
      {overallBand && maxClass > 0 && (
        <div style={{
          background: overallBand.color + "10",
          border: `1px solid ${overallBand.color}44`,
          borderRadius: 12,
          padding: "24px 28px",
        }}>
          <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: overallBand.color, marginBottom: 12 }}>
            Overall Impairment — Most Abnormal Measure
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
            <span style={{ fontSize: 48, fontWeight: 700, color: overallBand.color, lineHeight: 1 }}>
              {maxWPI}%
            </span>
            <div>
              <div style={{ fontSize: 18, color: "#111827", fontWeight: 500 }}>Whole Person Impairment</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{overallBand.label} · {overallBand.desc}</div>
            </div>
          </div>

          {(() => {
            const driver = results.find(r => r.wpi === maxWPI);
            return driver ? (
              <div style={{ marginTop: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 6, fontSize: 12, color: "#6b7280", border: "1px solid #e5e7eb" }}>
                Driven by <span style={{ color: overallBand.color, fontWeight: 600 }}>{driver.label} = {driver.value}%</span>
                {" "} → {driver.clsDef?.label}, WPI {driver.wpi}%
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Reference table */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 10 }}>
          Table 8 Reference Bands
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[
            { label: "FVC / FEV\u2081", rows: [
              { cls: "Class 1", range: "\u2265 80%", wpi: "0%" },
              { cls: "Class 2", range: "60\u201379%", wpi: "10\u201325%" },
              { cls: "Class 3", range: "51\u201359%", wpi: "26\u201350%" },
              { cls: "Class 4", range: "\u2264 50%", wpi: "51\u2013100%" },
            ]},
            { label: "D\u1d04O (DLCO)", rows: [
              { cls: "Class 1", range: "\u2265 70%", wpi: "0%" },
              { cls: "Class 2", range: "60\u201369%", wpi: "10\u201325%" },
              { cls: "Class 3", range: "41\u201359%", wpi: "26\u201350%" },
              { cls: "Class 4", range: "\u2264 40%", wpi: "51\u2013100%" },
            ]},
          ].map(col => (
            <div key={col.label} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "8px 14px", background: "#f9fafb", fontSize: 11, color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>
                {col.label}
              </div>
              {col.rows.map((row, i) => (
                <div key={row.cls} style={{
                  display: "flex", justifyContent: "space-between", padding: "6px 14px",
                  borderTop: i > 0 ? "1px solid #f3f4f6" : "none", fontSize: 12,
                }}>
                  <span style={{ color: BANDS[i]?.color, fontWeight: 500 }}>{row.cls}</span>
                  <span style={{ color: "#6b7280" }}>{row.range}</span>
                  <span style={{ color: "#9ca3af" }}>{row.wpi} WPI</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 12, color: "#9ca3af", lineHeight: 1.6 }}>
        * WPI interpolated linearly within each band. Overall WPI = highest single measure.
        <br/>Combine with other body system impairments using the Combined Values Chart (p.322).
      </div>
    </div>
  );
}
